// Client wrapper over the transcribe-video-link edge function. Pipes
// a TikTok / Instagram / YouTube / Reels URL through cobalt + Whisper
// and returns the spoken transcript. Caller then runs that transcript
// through parseRecipeText() to get a structured recipe.
//
// Two-step on the client (transcribe → parse) instead of one-shot so
// the user can SEE what the model heard and edit it before committing
// to a recipe parse — Whisper is good but not infallible on chef
// mumble + TikTok music underbed.
//
// YouTube-specific routing: as of mid-2025 every direct server-side
// path is bot-walled (LOGIN_REQUIRED, PO Token, Cloudflare data-
// center filter on downsub). The only free path that survives is to
// run the downsub piggyback FROM THE DEVICE itself — phone has a
// residential IP that Cloudflare doesn't block, and Capacitor's
// native HTTP bypasses the WebView's CORS check. For YouTube URLs
// we try fetchYouTubeTranscriptDirect first; if that fails (web
// platform, no captions, network error), fall through to the edge
// function so the existing error surface still works.

import { supabase } from "./supabase";
import { Capacitor } from "@capacitor/core";
import { extractYouTubeVideoId, fetchYouTubeTranscriptDirect } from "./youtubeTranscriptDirect";

/**
 * @param {object} args
 * @param {string} args.url — full https URL of the source video
 * @returns {Promise<{transcript:string, sourceUrl:string, durationSec:number|null}>}
 * @throws on edge-function error or empty transcript
 */
export async function transcribeVideoLink({ url } = {}) {
  if (typeof url !== "string" || !/^https?:\/\//i.test(url.trim())) {
    throw new Error("paste a full video URL (starting with https://)");
  }
  const trimmed = url.trim();

  // YouTube fast path — bypass the edge function entirely on native.
  // Phone IP + native HTTP defeats Cloudflare's data-center filter
  // and the WebView CORS check that block every server-side path.
  const ytId = extractYouTubeVideoId(trimmed);
  if (ytId && Capacitor.isNativePlatform?.()) {
    try {
      const r = await fetchYouTubeTranscriptDirect(ytId);
      return {
        transcript:  r.transcript,
        sourceUrl:   trimmed,
        durationSec: r.durationSec,
      };
    } catch (e) {
      // Fall through to the edge function so the standard error
      // surface still fires. The edge function will also fail for
      // YouTube (Cloudflare blocks Supabase IP) but the surfaced
      // detail string carries both reasons concatenated.
      console.warn("[transcribe] YouTube direct path failed, falling through:", e?.message || e);
    }
  }

  const { data, error } = await supabase.functions.invoke(
    "transcribe-video-link",
    { body: { url: trimmed } },
  );
  if (error) {
    let detail = "";
    const ctx = error.context;
    if (ctx && typeof ctx.text === "function") {
      try { detail = await ctx.text(); } catch { /* swallow */ }
    }
    let parsedDetail = "";
    if (detail) {
      try {
        const j = JSON.parse(detail);
        parsedDetail = j?.detail || j?.error || detail;
      } catch { parsedDetail = detail; }
    }
    throw new Error(
      parsedDetail
        ? `Couldn't transcribe: ${String(parsedDetail).slice(0, 1200)}`
        : `Couldn't transcribe: ${error.message || "unknown error"}`,
    );
  }
  if (!data?.transcript) {
    throw new Error("Couldn't transcribe: empty response");
  }
  return data;
}
